<template>
	<DataTable
		:value="rows"
		:loading="loading"
		class="p-datatable-sm p-datatable-documents editable-cells-table"
		:autoLayout="true"
		columnResizeMode="fit"
		dataKey="id"
		v-model:selection="selectedPrompts"
		editMode="cell"
		@cell-edit-init="onCellEditInit"
		@cell-edit-complete="onCellEditComplete"
		@row-dblclick="openEntryDialog($event.data)"
		:scrollable="true"
		scrollHeight="flex"
		:removableSort="true"
		filterDisplay="row"
		v-model:filters="filters"
		@filter="onFilter($event)"
		:paginator="true"
		:rows="pageSize"
		paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
		:rowsPerPageOptions="[25,50,100]"
		:currentPageReportTemplate="'{first} ' + $t('Squeeze.General.To') + ' {last} ' + $t('Squeeze.General.Of') + ' {totalRecords}'"
		@page="onChangePage"
	>
		<template #loading>
			{{ $t('Squeeze.General.Loading') }}
		</template>
		<template #empty>
			{{ $t('Squeeze.General.NoEntriesFound', { entryName: $t('Squeeze.Prompts.Prompts') }) }}
		</template>

		<!-- Selection for the export (super view only) -->
		<Column v-if="!context" selectionMode="multiple" headerClass="header-center" style="min-width: 3rem; max-width: 3rem; text-align: center;" />

		<!-- ID (not editable) -->
		<Column v-if="!context" field="id" header="ID" filterField="id" :sortable="true" :showFilterMenu="false" style="min-width: 4rem; max-width: 5rem;">
			<template #body="slotProps">
				{{ slotProps.data.id }}
			</template>
			<template #filter="{filterModel, filterCallback}">
				<InputText class="p-column-filter filter-field" type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" style="min-width: 3rem;" />
			</template>
		</Column>

		<!-- Document class -->
		<Column v-if="!context" field="documentClassId" filterField="documentClassId" :header="$t('Squeeze.XmlMapper.DocumentClass')" :sortable="true" :showFilterMenu="false" style="min-width: 10rem;">
			<template #body="slotProps">
				<div class="column-cell" v-tooltip.bottom="getDocumentClassName(slotProps.data.documentClassId)">
					{{ getDocumentClassName(slotProps.data.documentClassId) }}
				</div>
			</template>
			<template #editor="slotProps">
				<Dropdown
					v-model="slotProps.data.documentClassId"
					:options="documentClassOptions"
					optionValue="id"
					optionLabel="label"
					:filter="true"
					:showClear="true"
					:placeholder="$t('Squeeze.Prompts.Wildcard')"
					@change="onCellValueChanged"
					@show="showDropdownOverlay"
				/>
			</template>
			<template #filter="{filterModel, filterCallback}">
				<Dropdown
					v-model="filterModel.value"
					@change="filterCallback()"
					:options="documentClassOptions"
					optionValue="id"
					optionLabel="label"
					class="p-column-filter filter-field"
					:showClear="true"
					:filter="true"
					@show="showDropdownOverlay"
				/>
			</template>
		</Column>

		<!-- Company (Mandant) -->
		<Column field="company" filterField="company" :header="$t('Squeeze.XmlMapper.Company')" :sortable="true" :showFilterMenu="false" style="min-width: 9rem;">
			<template #body="slotProps">
				<div class="column-cell" v-tooltip.bottom="getOptionLabel(companyOptions, slotProps.data.company)">
					{{ getOptionLabel(companyOptions, slotProps.data.company) }}
				</div>
			</template>
			<template #editor="slotProps">
				<Dropdown
					v-if="companyOptions.length"
					v-model="slotProps.data.company"
					:options="companyOptions"
					optionValue="id"
					optionLabel="label"
					:filter="true"
					:showClear="true"
					:placeholder="$t('Squeeze.Prompts.Wildcard')"
					@change="onCellValueChanged"
					@show="showDropdownOverlay"
				/>
				<InputText v-else v-model="slotProps.data.company" :placeholder="$t('Squeeze.Prompts.Wildcard')" @input="onCellValueChanged" />
			</template>
			<template #filter="{filterModel, filterCallback}">
				<InputText class="p-column-filter filter-field" type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" style="min-width: 8rem;" />
			</template>
		</Column>

		<!-- Creditor (Geschäftspartner) -->
		<Column field="creditor" filterField="creditor" :header="$t('Squeeze.Prompts.Creditor')" :sortable="true" :showFilterMenu="false" style="min-width: 9rem;">
			<template #body="slotProps">
				<div class="column-cell" v-tooltip.bottom="getOptionLabel(creditorOptions, slotProps.data.creditor)">
					{{ getOptionLabel(creditorOptions, slotProps.data.creditor) }}
				</div>
			</template>
			<template #editor="slotProps">
				<Dropdown
					v-if="creditorOptions.length"
					v-model="slotProps.data.creditor"
					:options="creditorOptionsFor(slotProps.data)"
					optionValue="id"
					optionLabel="label"
					:filter="true"
					:showClear="true"
					:placeholder="$t('Squeeze.Prompts.Wildcard')"
					@change="onCellValueChanged"
					@show="showDropdownOverlay"
				/>
				<InputText v-else v-model="slotProps.data.creditor" :placeholder="$t('Squeeze.Prompts.Wildcard')" @input="onCellValueChanged" />
			</template>
			<template #filter="{filterModel, filterCallback}">
				<InputText class="p-column-filter filter-field" type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" style="min-width: 8rem;" />
			</template>
		</Column>

		<!-- Field name -->
		<Column v-if="!context" field="fieldName" filterField="fieldName" :header="$t('Squeeze.XmlMapper.FieldName')" :sortable="true" :showFilterMenu="false" style="min-width: 10rem;">
			<template #body="slotProps">
				<div class="column-cell" v-tooltip.bottom="slotProps.data.fieldName">
					{{ slotProps.data.fieldName }}
				</div>
			</template>
			<template #editor="slotProps">
				<Dropdown
					v-model="slotProps.data.fieldName"
					:options="fieldOptionsFor(slotProps.data)"
					optionValue="name"
					optionLabel="name"
					:filter="true"
					:filterFields="['name', 'translatedDescription', 'description']"
					:placeholder="$t('Squeeze.XmlMapper.FieldName')"
					@change="onCellValueChanged"
					@show="showDropdownOverlay"
				>
					<template #option="optionProps">
						<div>
							<span>{{ optionProps.option.name }}</span>
							<small v-if="getTranslatedDescription(optionProps.option)" class="field-option-description"> – {{ getTranslatedDescription(optionProps.option) }}</small>
						</div>
					</template>
				</Dropdown>
			</template>
			<template #filter="{filterModel, filterCallback}">
				<InputText class="p-column-filter filter-field" type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" style="min-width: 9rem;" />
			</template>
		</Column>

		<!-- LLM data type -->
		<Column field="dataType" filterField="dataType" :header="$t('Squeeze.MasterData.FileType')" :sortable="true" :showFilterMenu="false" style="min-width: 8rem; max-width: 9rem;">
			<template #body="slotProps">
				{{ getDataTypeLabel(slotProps.data.dataType) }}
			</template>
			<template #editor="slotProps">
				<Dropdown
					v-model="slotProps.data.dataType"
					:options="dataTypes"
					optionValue="id"
					optionLabel="text"
					@change="onCellValueChanged"
					@show="showDropdownOverlay"
				/>
			</template>
			<template #filter="{filterModel, filterCallback}">
				<Dropdown
					v-model="filterModel.value"
					@change="filterCallback()"
					:options="dataTypes"
					optionValue="id"
					optionLabel="text"
					class="p-column-filter filter-field"
					:showClear="true"
				/>
			</template>
		</Column>

		<!-- Prompt (truncated, tooltip shows full text) -->
		<Column field="prompt" filterField="prompt" :header="$t('Squeeze.Prompts.Prompt')" :sortable="true" :showFilterMenu="false" class="prompt-column" style="min-width: 12rem; max-width: 24rem;">
			<template #body="slotProps">
				<div class="column-cell" v-tooltip.bottom="slotProps.data.prompt">
					{{ slotProps.data.prompt }}
				</div>
			</template>
			<template #filter="{filterModel, filterCallback}">
				<InputText class="p-column-filter filter-field" type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" style="min-width: 10rem;" />
			</template>
		</Column>

		<!-- Active -->
		<Column field="active" filterField="active" :header="$t('Squeeze.System.Active')" :sortable="true" :showFilterMenu="false" headerClass="header-center" style="min-width: 6rem; max-width: 7rem; text-align: center;">
			<template #body="slotProps">
				<Checkbox v-model="slotProps.data.active" :binary="true" @change="onChangeActive(slotProps.data)" />
			</template>
		</Column>

		<!-- Edit & Delete (frozen to the right so the actions stay visible) -->
		<Column :exportable="false" frozen alignFrozen="right" style="min-width: 7rem; max-width: 7rem; text-align: right; z-index: 1;">
			<template #body="slotProps">
				<Button v-tooltip.bottom="$t('Squeeze.Locators.Edit')" icon="mdi mdi-pencil-outline" class="p-button-lg p-button-rounded p-button-success p-button-text" @click="openEntryDialog(slotProps.data)" />
				<Button v-tooltip.bottom="$t('Squeeze.General.Delete')" icon="mdi mdi-delete-outline" class="p-button-lg p-button-rounded p-button-danger p-button-text" @click="openDeleteDialog(slotProps.data)" />
			</template>
		</Column>

		<template #paginatorstart>
			<Button
				:label="$t('Squeeze.General.NewEntry')"
				type="button"
				icon="mdi mdi-plus"
				class="p-button"
				:disabled="!hasLlmClass"
				@click="openEntryDialog({})"
			/>
			<Button v-if="isFilterActive" :label="$t('Squeeze.General.ClearFilters')" type="button" icon="mdi mdi-filter-off-outline" class="p-button p-ml-2" @click="clearFilters" />
			<Button
				v-if="!context"
				:label="exportLabel"
				type="button"
				icon="mdi mdi-file-download-outline"
				class="p-button p-ml-2"
				:loading="exporting"
				:disabled="!selectedPrompts.length"
				@click="exportPrompts"
			/>
			<Button
				v-if="!context"
				:label="$t('Squeeze.Prompts.Import')"
				type="button"
				icon="mdi mdi-file-upload-outline"
				class="p-button p-ml-2"
				:loading="importing"
				@click="importPrompts"
			/>
		</template>
		<template #paginatorend></template>
	</DataTable>

	<!-- Delete dialog -->
	<DialogDelete :showDialog="deleteDialog" @onClose="deleteDialog = false" @onConfirm="deleteEntry()" />
</template>

<script lang="ts">
import {getTranslatedDescription} from "@/util/TranslationHelper";
/* eslint max-lines: off */
import {computed, defineComponent, onMounted, PropType, reactive, ref, watch} from "vue";
import {useI18n} from "vue-i18n";
import {useToast} from "primevue/usetoast";
import {useRoute} from "vue-router";
import Tooltip from "primevue/tooltip";
import DataTable from "@/components/datatable/DexDataTable.vue";
import {DataTableCellEditCompleteEvent, DataTableFilterEvent, DataTableFilterMeta, DataTablePageEvent} from "primevue/datatable";
import Column from "primevue/column";
import InputText from "primevue/inputtext";
import Dropdown from "primevue/dropdown";
import Checkbox from "primevue/checkbox";
import DialogDelete from "@/components/DialogDelete.vue";
import {FilterMatchMode} from "primevue/api";
import {DocumentClass, DocumentField, LlmPrompt, LlmPromptDataType, MasterDataCompanyEntry, MasterDataPartnerEntry} from "@dex/squeeze-client-ts";
import {showDropdownOverlay} from "@/util/StylesHelper";
import useSqueezeStore from "@/apps/squeeze/store";
import {TableSettings} from "@/util/TableSettings";
import {ClientManager} from "@/singletons/ClientManager";

export default defineComponent({
	name: "PromptList",
	components: {
		DataTable, Column, InputText, Dropdown, Checkbox, DialogDelete,
	},
	directives: {
		'tooltip': Tooltip,
	},
	props: {
		/** Rows to show */
		rows: {
			type: Array as PropType<LlmPrompt[]>,
			default: () => [],
		},
		/** LLM-enabled document classes (for lookup + editing) */
		documentClasses: {
			type: Array as PropType<DocumentClass[]>,
			default: () => [],
		},
		/** All selectable fields of the current tab (used for the inline field-name dropdown, filtered by document class) */
		allFields: {
			type: Array as PropType<DocumentField[]>,
			default: () => [],
		},
		/** Show loading indicator? */
		loading: {
			type: Boolean,
			default: false,
		},
		/** Is at least one class LLM-enabled? Controls the "New entry" button */
		hasLlmClass: {
			type: Boolean,
			default: false,
		},
		/** Context mode (embedded in a field): hides the id, document class and field name columns */
		context: {
			type: Boolean,
			default: false,
		},
		/** false = header data (Kopfdaten), true = line items (Positionen). Shown on the export button. */
		lineItem: {
			type: Boolean,
			default: false,
		},
		/** Master data options for the "Mandant" (company) field. Empty => free text input. */
		companyOptions: {
			type: Array as PropType<MasterDataCompanyEntry[]>,
			default: () => [],
		},
		/** Master data options for the "Geschäftspartner" (creditor) field. Empty => free text input. */
		creditorOptions: {
			type: Array as PropType<MasterDataPartnerEntry[]>,
			default: () => [],
		},
		/** Show a loading indicator on the export button? */
		exporting: {
			type: Boolean,
			default: false,
		},
		/** Show a loading indicator on the import button? */
		importing: {
			type: Boolean,
			default: false,
		},
	},
	emits: ["openEntryDialog", "deleteEntry", "inlineEdit", "exportPrompts", "importPrompts"],
	setup(props, {emit}) {
		const {t} = useI18n();
		const toast = useToast();
		const route = useRoute();
		const store = useSqueezeStore();

		/** User API for persisting table settings */
		const userApi = ClientManager.getInstance().squeeze.user;

		/** Current page size */
		const pageSize = ref<number>(25);

		/** Show the delete dialog? */
		const deleteDialog = ref<boolean>(false);

		/** Currently selected row */
		const rowData = reactive<LlmPrompt>({fieldName: '', dataType: LlmPromptDataType.Str, prompt: '', lineItem: false});

		/** Value of the cell before editing (to detect changes) */
		const cellChanged = ref<boolean>(false);

		/** Is at least one filter active? */
		const isFilterActive = ref<boolean>(false);

		/** Column filters */
		const filters = ref<DataTableFilterMeta>({});

		/** Prompts selected via checkbox. Only these are exported. */
		const selectedPrompts = ref<LlmPrompt[]>([]);

		/**
		 * Label of the export button. Names the data section of the tab, because only its
		 * selected prompts are exported and not all prompts of the system.
		 */
		const exportLabel = computed(() => {
			const section = props.lineItem ? t('Squeeze.XmlMapper.PositionData') : t('Squeeze.XmlMapper.HeadData');
			return t('Squeeze.Prompts.Export') + ' (' + section + ')';
		});

		/** Available LLM data types */
		const dataTypes = computed(() => {
			return [
				{id: LlmPromptDataType.Str, text: t('Squeeze.Prompts.DataTypes.Str')},
				{id: LlmPromptDataType.Int, text: t('Squeeze.Prompts.DataTypes.Int')},
				{id: LlmPromptDataType.Float, text: t('Squeeze.Prompts.DataTypes.Float')},
				{id: LlmPromptDataType.Bool, text: t('Squeeze.Prompts.DataTypes.Bool')},
				{id: LlmPromptDataType.Date, text: t('Squeeze.Prompts.DataTypes.Date')},
				{id: LlmPromptDataType.List, text: t('Squeeze.Prompts.DataTypes.List')},
			];
		});

		/** Document class options showing the translated description (fallback: description) */
		const documentClassOptions = computed(() => props.documentClasses.map(documentClass => ({
			id: documentClass.id,
			label: getTranslatedDescription(documentClass),
		})));

		/**
		 * Maps a stored master data id to its label (fallback: the raw value for legacy free text),
		 * or a wildcard (*) for empty values.
		 */
		const getOptionLabel = (options: {id: string; label: string}[], value?: string | null) => {
			if (value === null || value === undefined || value === '') {
				return '*';
			}
			const entry = options.find(option => option.id === value);
			return entry ? entry.label : value;
		};

		/**
		 * Creditor/debitor options filtered by the row's company (companyId -> companies.code).
		 * No company selected => all creditors/debitors.
		 */
		const creditorOptionsFor = (row: LlmPrompt): MasterDataPartnerEntry[] => {
			if (!row.company) {
				return props.creditorOptions;
			}
			const company = props.companyOptions.find(entry => entry.id === row.company);
			if (!company || company.code == null) {
				return props.creditorOptions;
			}
			return props.creditorOptions.filter(entry => entry.companyId === company.code);
		};

		/** Gets the (translated) description of a document class (or wildcard) */
		const getDocumentClassName = (documentClassId?: number | null) => {
			if (documentClassId === null || documentClassId === undefined) {
				return '*';
			}
			const documentClass = props.documentClasses.find(entry => entry.id === documentClassId);
			return documentClass ? getTranslatedDescription(documentClass) : String(documentClassId);
		};

		/**
		 * Field-name options for a row's inline dropdown.
		 * If the row has a document class, only that class's fields are shown; otherwise all fields.
		 */
		const fieldOptionsFor = (row: LlmPrompt): DocumentField[] => {
			if (row.documentClassId === null || row.documentClassId === undefined) {
				return props.allFields;
			}
			return props.allFields.filter(field => field.documentClassId == null || field.documentClassId === row.documentClassId);
		};

		/** Translated label for an LLM data type */
		const getDataTypeLabel = (dataType?: LlmPromptDataType) => {
			const entry = dataTypes.value.find(type => type.id === dataType);
			return entry ? entry.text : dataType;
		};

		/** Opens the edit / create dialog */
		const openEntryDialog = (data: LlmPrompt) => {
			emit("openEntryDialog", data);
		};

		/** Opens the delete dialog */
		const openDeleteDialog = (data: LlmPrompt) => {
			Object.assign(rowData, data);
			deleteDialog.value = true;
		};

		/** Confirms deletion of the current row */
		const deleteEntry = () => {
			deleteDialog.value = false;
			emit("deleteEntry", rowData);
		};

		/** Reset the change flag when an inline edit starts */
		const onCellEditInit = () => {
			cellChanged.value = false;
		};

		/** Flagged by an editor (@change/@input) when its value actually changed */
		const onCellValueChanged = () => {
			cellChanged.value = true;
		};

		/** Only persist the inline edit when a value actually changed */
		const onCellEditComplete = (event: DataTableCellEditCompleteEvent) => {
			if (cellChanged.value) {
				cellChanged.value = false;
				Object.assign(event.data, event.newData);
				emit("inlineEdit", event.data);
			}
		};

		/** Triggered when the active checkbox toggles */
		const onChangeActive = (data: LlmPrompt) => {
			emit("inlineEdit", data);
		};

		/** Starts the export of the selected prompts */
		const exportPrompts = () => {
			emit("exportPrompts", selectedPrompts.value);
		};

		/** Starts the import of a prompt file */
		const importPrompts = () => {
			emit("importPrompts");
		};

		/** Triggered when a filter changes */
		const onFilter = (event: DataTableFilterEvent) => {
			isFilterActive.value = event.filteredValue.length < props.rows.length;
		};

		/** Initialise the filters */
		const initFilters = () => {
			filters.value = {
				"id": {value: null, matchMode: FilterMatchMode.EQUALS},
				"documentClassId": {value: null, matchMode: FilterMatchMode.EQUALS},
				"company": {value: null, matchMode: FilterMatchMode.CONTAINS},
				"creditor": {value: null, matchMode: FilterMatchMode.CONTAINS},
				"fieldName": {value: null, matchMode: FilterMatchMode.CONTAINS},
				"dataType": {value: null, matchMode: FilterMatchMode.EQUALS},
				"prompt": {value: null, matchMode: FilterMatchMode.CONTAINS},
				"active": {value: null, matchMode: FilterMatchMode.EQUALS},
			};
		};

		/** Clears all filters */
		const clearFilters = () => {
			initFilters();
			isFilterActive.value = false;
		};

		initFilters();

		/** Triggered when the page (size) changes */
		const onChangePage = (event: DataTablePageEvent) => {
			pageSize.value = event.rows;
			TableSettings.saveTableListPagination(t, toast, store, userApi, route.name, event.rows);
		};

		onMounted(() => {
			pageSize.value = TableSettings.getTableListPagination(store, route.name);
		});

		/** Refresh selected prompts, if source data changes */
		watch(() => props.rows, () => {
			const availableIds = props.rows.map(prompt => prompt.id);
			selectedPrompts.value = selectedPrompts.value.filter(prompt => availableIds.includes(prompt.id));
		});

		return {
			getTranslatedDescription,
			t,
			filters,
			pageSize,
			deleteDialog,
			dataTypes,
			documentClassOptions,
			isFilterActive,
			getOptionLabel,
			creditorOptionsFor,
			getDocumentClassName,
			getDataTypeLabel,
			fieldOptionsFor,
			openEntryDialog,
			openDeleteDialog,
			deleteEntry,
			onCellEditInit,
			onCellValueChanged,
			onCellEditComplete,
			onChangeActive,
			onFilter,
			clearFilters,
			onChangePage,
			showDropdownOverlay,
			selectedPrompts,
			exportLabel,
			exportPrompts,
			importPrompts,
		};
	},
});
</script>

<style lang="scss" scoped>

// Table header
.p-datatable.p-datatable-documents ::v-deep(.p-datatable-thead tr th) {
	background-color: var(--dex-primary-light-color);
	padding: 0.6rem 0.6rem 0.2rem 0.3rem;
	border-bottom-color: var(--dex-primary-light-color);
	color: var(--dex-text-color);
}

.p-datatable.p-datatable-documents ::v-deep(.p-datatable-thead tr th:first-child) {
	padding: 0.6rem 0.6rem 0.2rem 0.6rem;
}

.p-datatable.p-datatable-documents ::v-deep(.p-datatable-thead .p-sortable-column-icon) {
	color: var(--dex-text-color);

	&:hover {
		color: var(--dex-text-color);
	}
}

::v-deep(td div.column-cell) {
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.field-option-description {
	color: var(--text-color-secondary);
}

::v-deep(td.p-editable-column.p-cell-editing) {
	padding-top: 0;
	padding-bottom: 0;
}

::v-deep(td.p-editable-column.p-cell-editing input),
::v-deep(td.p-editable-column.p-cell-editing .p-dropdown) {
	width: 100%;
}

::v-deep(.header-center div.p-column-header-content) {
	justify-content: center;
}

// Keep the prompt cell from pushing the action buttons out of the viewport
.prompt-column ::v-deep(div.column-cell) {
	max-width: 24rem;
}
</style>
